<script setup lang="ts">
import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getBootstrap, getProducts, type Category, type Product } from '../api';
import productsHero from '../assets/page-heroes/products.jpg';
import ProductCard from '../components/ProductCard.vue';
import { locale, text } from '../i18n';

const route = useRoute();
const router = useRouter();
const products = ref<Product[]>([]);
const categories = ref<Category[]>([]);
const loading = ref(true);
const error = ref('');
const search = ref(typeof route.query.search === 'string' ? route.query.search : '');

type ProductPrioritySetting = { topProductIds?: number[] };

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const category = typeof route.query.category === 'string' ? route.query.category : '';
    const [result, bootstrap] = await Promise.all([
      getProducts(locale.value, {
        pageSize: '100',
        ...(category ? { category } : {}),
        ...(typeof route.query.search === 'string' && route.query.search ? { search: route.query.search } : {})
      }),
      getBootstrap(locale.value).catch(() => null)
    ]);
    categories.value = bootstrap?.categories || [];
    const priority = (bootstrap?.settings['site.product-priority'] || {}) as ProductPrioritySetting;
    const priorityRank = new Map((priority.topProductIds || []).map((id, index) => [id, index]));
    products.value = [...result.data].sort((left, right) => {
      const leftRank = priorityRank.get(left.id);
      const rightRank = priorityRank.get(right.id);
      if (leftRank === undefined && rightRank === undefined) return 0;
      if (leftRank === undefined) return 1;
      if (rightRank === undefined) return -1;
      return leftRank - rightRank;
    });
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : text('加载产品失败', 'Unable to load products');
  } finally {
    loading.value = false;
  }
}

function applySearch() {
  void router.push({ query: { ...route.query, search: search.value.trim() || undefined } });
}

watch([() => route.fullPath, locale], load, { immediate: true });
</script>

<template>
  <div class="legacy-pages">
    <section class="portal-page-hero portal-page-hero--products">
      <img
        :src="productsHero"
        :alt="text('杭州技卫士人体安检设备与成像示例', 'JWS body screening equipment and imaging examples')"
        decoding="async"
        fetchpriority="high"
      >
      <div class="container">
        <span class="portal-eyebrow">{{ text('产品中心', 'Products') }}</span>
        <h1>{{ text('以场景约束选择合适的安检能力', 'Select screening capability around real site constraints') }}</h1>
        <p>{{ text('比较成像方式、通行效率、部署条件和系统协同能力，为项目建立清晰的设备选型边界。', 'Compare imaging methods, throughput, deployment conditions and system integration for a clear selection process.') }}</p>
      </div>
    </section>
    <section class="section">
      <div class="container catalog-layout">
        <aside class="catalog-filter">
          <h2>{{ text('产品分类', 'Categories') }}</h2>
          <RouterLink
            :to="{ path: '/products', query: route.query.search ? { search: route.query.search } : {} }"
            :class="{ active: !route.query.category }"
          >
            {{ text('全部产品', 'All products') }}
          </RouterLink>
          <RouterLink
            v-for="category in categories"
            :key="category.id"
            :to="{ path: '/products', query: { category: category.slug, ...(route.query.search ? { search: route.query.search } : {}) } }"
            :class="{ active: route.query.category === category.slug }"
          >
            {{ category.name }} <span>{{ category.productCount ?? '' }}</span>
          </RouterLink>
        </aside>
        <div class="catalog-main">
          <form
            class="catalog-search"
            role="search"
            @submit.prevent="applySearch"
          >
            <label
              for="product-search"
              class="sr-only"
            >{{ text('搜索产品', 'Search products') }}</label>
            <input
              id="product-search"
              v-model="search"
              type="search"
              :placeholder="text('输入产品名称或型号', 'Enter a product name or model')"
            >
            <button
              class="button"
              type="submit"
            >
              {{ text('搜索', 'Search') }}
            </button>
          </form>
          <p class="result-summary">
            {{ text(`当前显示 ${products.length} 个产品`, `${products.length} products`) }}
          </p>
          <div
            v-if="products.length"
            class="product-grid product-grid--catalog"
          >
            <ProductCard
              v-for="product in products"
              :key="product.id"
              :product="product"
            />
          </div>
          <div
            v-else-if="loading"
            class="loading-state"
          >
            <i /><span>{{ text('正在加载产品…', 'Loading products…') }}</span>
          </div>
          <p
            v-else
            class="empty-state"
          >
            {{ error || text('当前条件下没有已发布产品。', 'No published products match these filters.') }}
          </p>
        </div>
      </div>
    </section>
  </div>
</template>
